import axios, { AxiosResponse } from 'axios';
import baseAPI from '@/api/base';
import type { ChatUploadTicket, CreateChatGroup, PresignChatUpload, SendChatMessage } from '@/dtos/chat.dto';
import {
  CHAT_CONVERSATION_URL,
  CREATE_CHAT_DIRECT_URL,
  CREATE_CHAT_GROUP_URL,
  GET_CHAT_CONVERSATIONS_URL,
  GET_CHAT_DIRECTORY_URL,
  GET_CHAT_NOTIFICATIONS_URL,
  GET_CHAT_UNREAD_URL,
  MARK_CHAT_NOTIFICATIONS_READ_URL,
  MARK_CHAT_NOTIFICATIONS_SEEN_URL,
  PRESIGN_CHAT_UPLOAD_URL,
  DISCARD_CHAT_UPLOAD_URL,
} from '@/utils/urls';

class Chat {
  static getDirectory = async (params: { q?: string } = {}): Promise<AxiosResponse> => baseAPI.get(GET_CHAT_DIRECTORY_URL, { params });

  static getUnread = async (): Promise<AxiosResponse> => baseAPI.get(GET_CHAT_UNREAD_URL, { silent: true });

  static getConversations = async (): Promise<AxiosResponse> => baseAPI.get(GET_CHAT_CONVERSATIONS_URL);

  static getConversation = async (id: string): Promise<AxiosResponse> => baseAPI.get(`${CHAT_CONVERSATION_URL}/${id}`);

  static createDirect = async (employeeId: string): Promise<AxiosResponse> => baseAPI.post(CREATE_CHAT_DIRECT_URL, { employeeId });

  static createGroup = async (payload: CreateChatGroup): Promise<AxiosResponse> => baseAPI.post(CREATE_CHAT_GROUP_URL, payload);

  static updateConversation = async (id: string, payload: { name?: string; description?: string }): Promise<AxiosResponse> =>
    baseAPI.put(`${CHAT_CONVERSATION_URL}/${id}`, payload);

  static addMembers = async (id: string, memberIds: string[]): Promise<AxiosResponse> =>
    baseAPI.post(`${CHAT_CONVERSATION_URL}/${id}/members`, { memberIds });

  static removeMember = async (id: string, employeeId: string): Promise<AxiosResponse> =>
    baseAPI.delete(`${CHAT_CONVERSATION_URL}/${id}/members/${employeeId}`);

  static leaveConversation = async (id: string): Promise<AxiosResponse> => baseAPI.post(`${CHAT_CONVERSATION_URL}/${id}/leave`);

  static setMuted = async (id: string, isMuted: boolean): Promise<AxiosResponse> =>
    baseAPI.patch(`${CHAT_CONVERSATION_URL}/${id}/mute`, { isMuted });

  /** Fires often and quietly; a failure is retried by the next read. */
  static markRead = async (id: string): Promise<AxiosResponse> =>
    baseAPI.post(`${CHAT_CONVERSATION_URL}/${id}/read`, {}, { silent: true });

  static getMessages = async (id: string, params: { before?: string | null; limit?: number } = {}): Promise<AxiosResponse> =>
    baseAPI.get(`${CHAT_CONVERSATION_URL}/${id}/messages`, { params: { limit: params.limit ?? 50, ...(params.before ? { before: params.before } : {}) } });

  /** Failures are shown on the bubble itself, not as a toast. */
  static sendMessage = async (payload: SendChatMessage): Promise<AxiosResponse> => {
    const body = { body: payload.body, clientId: payload.clientId, file: payload.file, replyToId: payload.replyToId };
    return baseAPI.post(`${CHAT_CONVERSATION_URL}/${payload.conversationId}/messages`, body, { silent: true });
  };

  static deleteMessage = async (conversationId: string, messageId: string): Promise<AxiosResponse> =>
    baseAPI.delete(`${CHAT_CONVERSATION_URL}/${conversationId}/messages/${messageId}`);

  static getFiles = async (id: string): Promise<AxiosResponse> => baseAPI.get(`${CHAT_CONVERSATION_URL}/${id}/files`);

  static presignUpload = async (payload: PresignChatUpload): Promise<AxiosResponse> => baseAPI.post(PRESIGN_CHAT_UPLOAD_URL, payload);

  /** An uploaded file the writer removed before sending; best effort, never a toast. */
  static discardUpload = async (key: string): Promise<AxiosResponse> =>
    baseAPI.delete(DISCARD_CHAT_UPLOAD_URL, { data: { key }, silent: true });

  /**
   * Uploads straight to the storage bucket with the presigned ticket. A plain axios
   * call on purpose: the API client would add the session header, which storage rejects.
   */
  static uploadToStorage = async (
    ticket: ChatUploadTicket,
    file: File,
    onProgress: (fraction: number) => void,
    signal?: AbortSignal,
  ): Promise<void> => {
    await axios.put(ticket.uploadUrl, file, {
      headers: ticket.headers,
      timeout: 0,
      signal,
      onUploadProgress: (event) => {
        const total = event.total ?? file.size;
        if (total > 0) onProgress(Math.min(event.loaded / total, 1));
      },
    });
  };

  static getNotifications = async (limit = 30): Promise<AxiosResponse> => baseAPI.get(GET_CHAT_NOTIFICATIONS_URL, { params: { limit }, silent: true });

  static markNotificationsSeen = async (): Promise<AxiosResponse> => baseAPI.patch(MARK_CHAT_NOTIFICATIONS_SEEN_URL, {}, { silent: true });

  static markNotificationsRead = async (payload: { ids?: string[]; all?: boolean }): Promise<AxiosResponse> =>
    baseAPI.patch(MARK_CHAT_NOTIFICATIONS_READ_URL, payload, { silent: true });
}

export default Chat;
